import {
  computedAsync,
  tryOnMounted,
  useBroadcastChannel,
  useEventListener,
  useWindowFocus,
  whenever,
} from '@vueuse/core';
import axios from 'axios';
import { toRef, toValue, watch, computed, type MaybeRef, type Ref } from 'vue';

import { trans } from '@/Plugins/i18n';
import { isInInputContext } from '@/Utils/misc';
import { route } from 'ziggy-js';

import type { DocumentableInfo, DocumentResource } from '@/Models/Document';

type DocumentsBroadcastMessage = { documentIds: string[]; documentable: DocumentableInfo };

export type DocumentWindowNavigationAction =
  | 'nextPage'
  | 'previousPage'
  | 'nextDocument'
  | 'previousDocument'
  | 'jumpToInvoice';

const documentsChannel = 'documents';
const documentNavigationChannel = 'document-window-navigation';

export function useDocumentsReceiver(): {
  documents: Ref<DocumentResource[]>;
  documentableInfo: Ref<DocumentableInfo | null>;
} {
  const { data } = useBroadcastChannel<
    DocumentsBroadcastMessage | undefined,
    DocumentsBroadcastMessage
  >({ name: documentsChannel });

  const documentable = computed(() => data.value?.documentable ?? null);
  const documents = computedAsync<DocumentResource[]>(async function () {
    if (!data.value?.documentIds.length) {
      return [];
    }
    const response = await axios.get<DocumentResource[]>(
      route('documents.index', { documentIds: data.value.documentIds }),
    );
    return response.data;
  }, []);

  return {
    documents,
    documentableInfo: documentable,
  };
}

export function useDocumentsBroadcaster(
  documents: MaybeRef<DocumentResource[]>,
  documentable: MaybeRef<DocumentableInfo | null>,
): void {
  const { post } = useBroadcastChannel<
    DocumentsBroadcastMessage | undefined,
    DocumentsBroadcastMessage
  >({ name: documentsChannel });
  const focused = useWindowFocus();
  const documentIds = computed(() => toValue(documents).map((document) => document.id));
  const documentableRef = toRef(documentable);

  function trySend() {
    if (focused.value && documentableRef.value) {
      post({
        documentIds: documentIds.value,
        documentable: {
          ...documentableRef.value,
        },
      });
    }
  }

  tryOnMounted(trySend);
  watch([focused, documentIds, documentableRef], () => {
    trySend();
  });
}

export function useDocumentWindowHotkeys() {
  const { post } = useBroadcastChannel<
    DocumentWindowNavigationAction | undefined,
    DocumentWindowNavigationAction
  >({ name: documentNavigationChannel });
  useEventListener(
    'keydown',
    (event: KeyboardEvent) => {
      if (!event.key || isInInputContext()) {
        return;
      }

      const key = event.key.toLowerCase();

      // check shift is pressed but no other modifier key
      if (!event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) {
        return;
      }

      switch (key) {
        case 'j': {
          post('previousDocument');
          event.stopPropagation();
          break;
        }
        case 'l': {
          post('nextDocument');
          event.stopPropagation();
          break;
        }
        case 'i': {
          post('previousPage');
          event.stopPropagation();
          break;
        }
        case 'k': {
          post('nextPage');
          event.stopPropagation();
          break;
        }
        case 'u': {
          post('jumpToInvoice');
          event.stopPropagation();
          break;
        }
      }
    },
    { capture: true },
  );
}

export const documentWindowHotkeyHints: Ref<
  Record<DocumentWindowNavigationAction, { hotkey: string; hint: string }>
> = computed(() => ({
  previousDocument: {
    hotkey: 'J',
    hint: trans('previous_document'),
  },
  nextDocument: {
    hotkey: 'L',
    hint: trans('next_document'),
  },
  previousPage: {
    hotkey: 'I',
    hint: trans('previous_page'),
  },
  nextPage: {
    hotkey: 'K',
    hint: trans('next_page'),
  },
  jumpToInvoice: {
    hotkey: 'U',
    hint: trans('jump_to_invoice'),
  },
}));

export function useDocumentWindowNavigation(
  navigationActions: Record<DocumentWindowNavigationAction, () => void>,
) {
  const { data } = useBroadcastChannel<
    DocumentWindowNavigationAction | undefined,
    DocumentWindowNavigationAction
  >({ name: documentNavigationChannel });

  whenever(data, () => {
    navigationActions[data.value!]();
    data.value = undefined; // reset action
  });
}
